/* eslint-disable */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { getData, putData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

import MediaLibraryHeader from "./Header/MediaLibraryHeader";
import DeleteWarningModal from "components/Common/Modal/DeleteWarningModal";
import FilePreviewModal from "./Modal/FilePreviewModal";
import UploadFilePopup from "./Popup/UploadFilePopup";

// ── helpers ──────────────────────────────────────────────────────────────────

const getExtension = (name = "") => (name.split(".").pop() || "").toLowerCase();

const FILE_TYPES = {
  image: ["jpg", "jpeg", "png", "gif", "svg", "webp", "bmp"],
  video: ["mp4", "avi", "mov", "webm", "mkv", "flv"],
  audio: ["m4a", "mp3", "wav", "ogg", "aac", "flac"],
  pdf: ["pdf"],
  word: ["doc", "docx"],
  excel: ["xls", "xlsx", "csv"],
  ppt: ["ppt", "pptx"],
  text: ["txt", "rtf"],
  code: ["py", "js", "ts", "xml", "json", "html", "css", "dll"],
};

const getFileType = (name) => {
  const ext = getExtension(name);
  for (const [type, exts] of Object.entries(FILE_TYPES)) {
    if (exts.includes(ext)) return type;
  }
  return "other";
};

const TYPE_META = {
  image: { icon: "image", color: "#10B981", bg: "#D1FAE5", label: "Image" },
  video: {
    icon: "play_circle",
    color: "#8B5CF6",
    bg: "#EDE9FE",
    label: "Video",
  },
  audio: {
    icon: "graphic_eq",
    color: "#F59E0B",
    bg: "#FEF3C7",
    label: "Audio",
  },
  pdf: {
    icon: "picture_as_pdf",
    color: "#EF4444",
    bg: "#FEE2E2",
    label: "PDF",
  },
  word: { icon: "description", color: "#2563EB", bg: "#DBEAFE", label: "Word" },
  excel: {
    icon: "table_chart",
    color: "#059669",
    bg: "#D1FAE5",
    label: "Excel",
  },
  ppt: { icon: "co_present", color: "#EA580C", bg: "#FFEDD5", label: "PPT" },
  text: { icon: "article", color: "#64748B", bg: "#F1F5F9", label: "Text" },
  code: { icon: "code", color: "#7C3AED", bg: "#EDE9FE", label: "Code" },
  other: {
    icon: "insert_drive_file",
    color: "#6B7280",
    bg: "#F3F4F6",
    label: "File",
  },
};

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const truncateName = (name = "", max = 22) =>
  name.length > max ? name.slice(0, max) + "…" : name;

const buildFileUrl = (path = "") => url.SERVER_URL.replace(/\/$/, "") + path;

// ── sub-components ────────────────────────────────────────────────────────────

const ImageThumbnail = ({ fileUrl, name }) => {
  const [errored, setErrored] = useState(false);
  const meta = TYPE_META.image;

  if (errored) {
    return (
      <div
        className="d-flex align-items-center justify-content-center w-100 h-100"
        style={{ background: meta.bg }}
      >
        <span
          className="material-symbols-outlined"
          style={{ color: meta.color, fontSize: 40 }}
        >
          broken_image
        </span>
      </div>
    );
  }

  return (
    <img
      src={fileUrl}
      alt={name}
      onError={() => setErrored(true)}
      style={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
        display: "block",
      }}
    />
  );
};

const FileTypePlaceholder = ({ type }) => {
  const meta = TYPE_META[type] || TYPE_META.other;
  return (
    <div
      className="d-flex flex-column align-items-center justify-content-center w-100 h-100 gap-1"
      style={{ background: meta.bg }}
    >
      <span
        className="material-symbols-outlined"
        style={{ color: meta.color, fontSize: 44 }}
      >
        {meta.icon}
      </span>
      <span
        className="badge fw-semibold text-uppercase"
        style={{
          background: meta.color,
          color: "#fff",
          fontSize: "0.65rem",
          letterSpacing: "0.05em",
        }}
      >
        {meta.label}
      </span>
    </div>
  );
};

// ── MediaCard with Checkbox ───────────────────────────────────────────────────

const MediaCard = ({ item, onPreview, isSelected, onToggleSelect }) => {
  const fileType = getFileType(item.name);
  const isImage = fileType === "image";
  const fileUrl = buildFileUrl(item.path);
  const meta = TYPE_META[fileType] || TYPE_META.other;

  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    onToggleSelect(item._id);
  };

  const handleCardClick = () => {
    if (onPreview) onPreview(item);
  };

  return (
    <div
      className="position-relative rounded-3 overflow-hidden border"
      style={{
        background: "#fff",
        boxShadow: isSelected
          ? `0 0 0 1px var(--bs-primary), 0 4px 16px rgba(var(--bs-primary-rgb),0.15)`
          : "0 1px 4px rgba(0,0,0,0.08)",
        transition: "box-shadow 0.2s",
        cursor: "pointer",
        outline: isSelected ? "0.5px solid var(--bs-primary)" : "none",
      }}
      onClick={handleCardClick}
    >
      {/* ── Checkbox (top-left) ── */}
      <div
        className="card-checkbox-wrap position-absolute top-0 start-0 m-2"
        style={{ zIndex: 10, transition: "opacity 0.15s" }}
        onClick={handleCheckboxClick}
      >
        <div
          style={{
            width: 22,
            height: 22,
            borderRadius: 6,
            border: `2px solid ${isSelected ? `var(--bs-primary)` : "#fff"}`,
            background: isSelected
              ? `var(--bs-primary)`
              : "rgba(255,255,255,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
            backdropFilter: "blur(4px)",
            transition: "background 0.15s, border 0.15s",
          }}
        >
          {isSelected && (
            <span
              className="material-symbols-outlined"
              style={{ color: "#fff", fontSize: 15, fontWeight: 700 }}
            >
              check
            </span>
          )}
        </div>
      </div>

      {/* Thumbnail area */}
      <div
        style={{
          height: 160,
          overflow: "hidden",
          background: "#f8fafc",
          position: "relative",
        }}
      >
        {isImage ? (
          <ImageThumbnail fileUrl={fileUrl} name={item.name} />
        ) : (
          <FileTypePlaceholder type={fileType} />
        )}

        {isImage && (
          <span
            className="position-absolute top-0 end-0 m-1 badge"
            style={{
              background: "rgba(0,0,0,0.45)",
              color: "#fff",
              fontSize: "0.6rem",
              backdropFilter: "blur(4px)",
            }}
          >
            {getExtension(item.name).toUpperCase()}
          </span>
        )}
      </div>

      {/* Info area */}
      <div className="px-2 py-2" style={{ borderTop: "1px solid #f1f5f9" }}>
        <p
          className="mb-0 fw-semibold text-truncate"
          style={{ fontSize: "0.78rem", color: "#1e293b" }}
          title={item.name}
        >
          {item.name}
        </p>
        <div className="d-flex align-items-center justify-content-between mt-1">
          <span style={{ fontSize: "0.68rem", color: "#94a3b8" }}>
            {formatDate(item.createdAt)}
          </span>
          <span
            className="badge"
            style={{
              background: meta.bg,
              color: meta.color,
              fontSize: "0.6rem",
              fontWeight: 600,
              border: `1px solid ${meta.color}33`,
            }}
          >
            {meta.label}
          </span>
        </div>
      </div>
    </div>
  );
};

// ── Empty State ───────────────────────────────────────────────────────────────

const EmptyState = ({ selectedFilter, onClearFilter, t }) => (
  <div className="text-center py-5">
    <style>{`
      @keyframes es-fade-up {
        from { opacity: 0; transform: translateY(16px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      .es-text { animation: es-fade-up 0.5s ease both; animation-delay: 0.15s; opacity: 0; }
    `}</style>

    <div
      className="d-flex flex-column align-items-center justify-content-center"
      style={{ minHeight: 360 }}
    >
      {/* Icon */}
      <div className="d-inline-flex align-items-center justify-content-center mb-4">
        <div
          className="d-flex align-items-center justify-content-center rounded-circle"
          style={{
            width: 92,
            height: 92,
            background: "linear-gradient(145deg, #EEF2FF 0%, #E0E7FF 100%)",
            boxShadow:
              "0 8px 32px rgba(99,102,241,0.18), 0 2px 8px rgba(99,102,241,0.10)",
          }}
        >
          <svg
            width="46"
            height="46"
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect x="5" y="12" width="26" height="32" rx="3.5" fill="#C7D2FE" />
            <rect x="9" y="8" width="26" height="32" rx="3.5" fill="#A5B4FC" />
            <rect
              x="13"
              y="4"
              width="26"
              height="32"
              rx="3.5"
              fill="white"
              stroke="#818CF8"
              strokeWidth="1.5"
            />
            <circle
              cx="27"
              cy="14"
              r="4.5"
              fill="#EDE9FE"
              stroke="#818CF8"
              strokeWidth="1.2"
            />
            <path
              d="M15 29 L21 21 L26 26.5 L30 22 L39 29"
              stroke="#818CF8"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
            <line
              x1="17"
              y1="35"
              x2="29"
              y2="35"
              stroke="#C7D2FE"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <line
              x1="17"
              y1="39"
              x2="25"
              y2="39"
              stroke="#C7D2FE"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <circle cx="38" cy="10" r="7" fill="#818CF8" />
            <path
              d="M38 13.5 L38 7.5 M35.5 10 L38 7.5 L40.5 10"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      {/* Text + CTA */}
      <div className="es-text">
        <h5
          className="fw-bold mb-2"
          style={{
            color: "#1E293B",
            fontSize: "1.1rem",
            letterSpacing: "-0.01em",
          }}
        >
          {selectedFilter !== "all"
            ? t("No files match this filter")
            : t("Your media library is empty")}
        </h5>
        <p
          className="mb-4 mx-auto"
          style={{
            color: "#94A3B8",
            fontSize: "0.85rem",
            maxWidth: 280,
            lineHeight: 1.65,
          }}
        >
          {selectedFilter !== "all"
            ? t("Try switching to a different category or upload new files.")
            : t(
                "Upload images, videos, documents and more to build your library.",
              )}
        </p>

        {/* Primary CTA */}
        <button
          type="button"
          className="btn btn-primary d-inline-flex align-items-center gap-2 px-4 py-2"
          style={{
            borderRadius: 10,
            fontWeight: 600,
            fontSize: "0.875rem",
            boxShadow: "0 4px 14px rgba(var(--bs-primary-rgb),0.35)",
          }}
          data-bs-toggle="offcanvas"
          data-bs-target="#uploadFilePopup"
        >
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
            cloud_upload
          </span>
          {t("Upload Files")}
        </button>

        {/* Secondary — clear filter */}
        {selectedFilter !== "all" && (
          <div className="mt-3">
            <button
              type="button"
              className="btn btn-link text-muted p-0"
              style={{ fontSize: "0.8rem", textDecoration: "underline" }}
              onClick={onClearFilter}
            >
              {t("Show all files")}
            </button>
          </div>
        )}
      </div>
    </div>
  </div>
);

const MediaLibraryBody = () => {
  const token = localStorage.getItem("token");
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [mediaList, setMediaList] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // ── Selection handlers ────────────────────────────────────────────────────

  const handleToggleSelect = (id) => {
    setSelectedItemIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    const filteredList = getFilteredMediaList();
    if (selectedItemIds.length === filteredList.length) {
      setSelectedItemIds([]);
    } else {
      setSelectedItemIds(filteredList.map((item) => item._id));
    }
  };

  const handleClearSelection = () => setSelectedItemIds([]);

  // ── Preview ───────────────────────────────────────────────────────────────

  const handlePreview = (item) => {
    setSelectedItem(item);
    const modalEl = document.getElementById("filePreviewModal");
    if (window.bootstrap && modalEl) {
      window.bootstrap.Modal.getOrCreateInstance(modalEl).show();
    }
  };

  // ── Data fetching ─────────────────────────────────────────────────────────

  const getMediaList = async () => {
    try {
      setIsLoading(true);
      let requestUrl =
        url.API_BASE_URL + url.API_GET_ALL_UPLOADS + `?token=${token}`;

      console.log("requestUrl in media library------>", requestUrl);

      const response = await getData(requestUrl);

      if (response.status) {
        setMediaList(response.data);
      }
    } catch (error) {
      console.log(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteMediaHandler = async () => {
    try {
      let mediaData = { uploadids: selectedItemIds };
      let requestURL =
        url.API_BASE_URL + url.API_DELETE_BULK_UPLOADS + `?token=${token}`;
      const response = await putData(requestURL, mediaData);
      console.log(response);
      if (response.status) {
        setSelectedItemIds([]);
        getMediaList();
      }
    } catch (error) {
      console.log(error.message);
    }
  };

  const resetFilter = () => {
    setSelectedFilter("all");
    setSelectedItemIds([]);
    getMediaList();
  };

  useEffect(() => {
    getMediaList();
  }, []);

  // ── Filter logic ──────────────────────────────────────────────────────────
  const getFilteredMediaList = () => {
    let list = mediaList;
    // Filter by media type
    if (selectedFilter !== "all") {
      if (selectedFilter === "documents") {
        list = list.filter((item) =>
          ["pdf", "word", "excel", "ppt", "text", "code"].includes(
            getFileType(item.name),
          ),
        );
      } else {
        list = list.filter((item) => getFileType(item.name) === selectedFilter);
      }
    }
    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      list = list.filter((item) => item.name.toLowerCase().includes(term));
    }
    return list;
  };

  const filteredList = getFilteredMediaList();
  const allSelected =
    filteredList.length > 0 && selectedItemIds.length === filteredList.length;

  return (
    <div id="content_wrapper">
      <section className="crm-wrapper crm-conversation-wrapper bg-white pb-5">
        <div className="container-fluid px-lg-5">
          <MediaLibraryHeader
            reloadList={resetFilter}
            selectedFilter={selectedFilter}
            onFilterChange={setSelectedFilter}
            selectedItemIds={selectedItemIds}
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
          />

          {/* ── Selection action bar ── */}
          {!isLoading && filteredList.length > 0 && (
            <div className="d-flex align-items-center gap-3 mt-3 mb-1 flex-wrap">
              <div
                className="d-flex align-items-center gap-2"
                style={{ cursor: "pointer", userSelect: "none" }}
                onClick={handleSelectAll}
              >
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 5,
                    border: `2px solid ${allSelected ? `var(--bs-primary)` : "#94a3b8"}`,
                    background: allSelected ? `var(--bs-primary)` : "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "background 0.15s",
                  }}
                >
                  {allSelected && (
                    <span
                      className="material-symbols-outlined"
                      style={{ color: "#fff", fontSize: 14 }}
                    >
                      check
                    </span>
                  )}
                  {!allSelected && selectedItemIds.length > 0 && (
                    <span
                      style={{
                        width: 10,
                        height: 2,
                        background: `var(--bs-primary)`,
                        borderRadius: 2,
                        display: "block",
                      }}
                    />
                  )}
                </div>
                <span className="text-muted small">
                  {t("Select All")} ({filteredList.length})
                </span>
              </div>

              {selectedItemIds.length > 0 && (
                <div className="d-flex align-items-center gap-2">
                  <span
                    className="badge"
                    style={{
                      background: `var(--bs-primary)`,
                      color: "#fff",
                      fontSize: "0.75rem",
                    }}
                  >
                    {selectedItemIds.length} {t("selected")}
                  </span>
                  <button
                    className="btn btn-sm btn-outline-secondary py-0 px-2"
                    style={{ fontSize: "0.72rem" }}
                    onClick={handleClearSelection}
                  >
                    {t("Clear")}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Loading Spinner */}
          {isLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3 text-muted">{t("Loading media files...")}</p>
            </div>
          ) : filteredList.length === 0 ? (
            /* ── Beautiful Empty State ── */
            <EmptyState
              selectedFilter={selectedFilter}
              onClearFilter={() => setSelectedFilter("all")}
              t={t}
            />
          ) : (
            <div className="mt-3">
              <p className="text-muted small mb-3">
                {filteredList.length} {t("item(s)")}
              </p>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                  gap: "16px",
                }}
              >
                {filteredList.map((item) => (
                  <MediaCard
                    key={item._id}
                    item={item}
                    onPreview={handlePreview}
                    isSelected={selectedItemIds.includes(item._id)}
                    onToggleSelect={handleToggleSelect}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* modal and offcanvas section */}
        <UploadFilePopup afterUpload={getMediaList} />
        <FilePreviewModal
          selectedItem={selectedItem}
          setSelectedItem={setSelectedItem}
        />
        <DeleteWarningModal
          onDelete={deleteMediaHandler}
          onCancelDelete={() => setSelectedItemIds([])}
        />
      </section>
    </div>
  );
};

export default MediaLibraryBody;
